import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { t, tx } from '../../i18n';
import { fmtDate, fmtDateTime, fmtKB } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { Preview } from '../belege/BelegSheet';
import { Sheet } from '../belege/Sheet';
import { useCloseSheet } from '../lohn/Pages';
import type { RequestDetail } from './types';
import { ShareFile } from '../../components/ShareFile';

// Angeforderte Unterlage ansehen (?anfrage=<id>): Stand, Dateien der Kanzlei (Vorschau, Herunterladen), Kommentare.
export function RequestSheetHost() {
  const [params] = useSearchParams();
  const close = useCloseSheet('anfrage');
  const id = params.get('anfrage');
  return id ? <RequestSheet key={id} id={id} onClose={close} /> : null;
}

function statusText(r: RequestDetail, ask: boolean): string {
  if (r.status === 'erledigt') {
    return r.doneAt ? t('Erledigt von Ihrer Kanzlei am {date}', { date: fmtDate(r.doneAt) }) : t('Erledigt von Ihrer Kanzlei');
  }
  if (ask) return t('Ihre Kanzlei hat eine Rückfrage. Bitte antworten Sie unten.');
  if (r.files.length) return t('Ihre Kanzlei hat die Unterlage hochgeladen.');
  return t('Ihre Kanzlei kümmert sich darum und lädt die Unterlage hier hoch.');
}

function RequestSheet({ id, onClose }: { id: string; onClose(): void }) {
  const res = useApi<RequestDetail>(`/api/app/anfrage/${id}`, { live: ['anfragen'] });
  const [shown, setShown] = useState<string | null>(null);
  const r = res.data;

  // Meldungen der Kanzlei zu dieser Anfrage (Datei, Kommentar, erledigt) gelten als gelesen, sobald das Fenster sie zeigt
  const staffComments = r?.comments.filter((c) => c.side === 'staff').length ?? 0;
  const files = r?.files.length ?? 0;
  const done = r?.status === 'erledigt';
  useEffect(() => {
    if (staffComments > 0 || files > 0 || done) api.post(`/api/app/anfrage/${id}/read`).catch(() => {});
  }, [id, staffComments, files, done]);

  if (!r) {
    return (
      <Sheet title={t('Unterlage')} onClose={onClose}>
        {res.error ? <p className="meta">{res.error}</p> : <div className="spinner" aria-label={t('Lädt')} />}
      </Sheet>
    );
  }

  const last = r.comments.at(-1);
  const lastFile = r.files.at(-1);
  const ask =
    r.status === 'offen' &&
    last?.side === 'staff' &&
    (!lastFile || new Date(last.createdAt).getTime() > new Date(lastFile.createdAt).getTime());
  const file = r.files.find((f) => f.id === shown) ?? r.files[0];
  const fileUrl = file ? `/api/app/anfrage/${r.id}/file/${file.id}` : '';

  return (
    <Sheet title={tx(r.label)} onClose={onClose}>
      <div className="meta">
        {r.employee ? `${r.employee.name} · ` : ''}
        {r.clientName} · {t('Nr. {nr}', { nr: r.nr })} ·{' '}
        {r.createdBy
          ? t('angefordert am {date} von {name}', { date: fmtDate(r.createdAt), name: r.createdBy })
          : t('angefordert am {date}', { date: fmtDate(r.createdAt) })}
      </div>
      {r.note && <div className="rq-note">{t('Ihr Wunsch: „{note}“', { note: r.note })}</div>}
      <p className={`bstat${ask ? ' ask' : r.status === 'erledigt' || r.files.length ? ' done' : ''}`} role="status">
        {statusText(r, ask)}
      </p>
      {file && (
        <>
          <Preview key={file.id} url={fileUrl} mime={file.mime} name={file.name} />
          {r.files.length > 1 && (
            <div className="files rq-files">
              <h3>{t('Dateien ({n})', { n: r.files.length })}</h3>
              {r.files.map((f) => (
                <button key={f.id} type="button" className="file" aria-pressed={f.id === file.id} onClick={() => setShown(f.id)}>
                  <span>
                    {f.name}
                    <small className="fsub">
                      {fmtKB(f.size)} · {t('am {date}', { date: fmtDate(f.createdAt) })}
                    </small>
                  </span>
                  <span className={f.id === file.id ? 'ok' : 'chev'}>
                    {f.id === file.id ? t('angezeigt') : `${t('Ansehen')} ›`}
                  </span>
                </button>
              ))}
            </div>
          )}
          <div className="row">
            <a className="btn2 dark" href={`${fileUrl}?download=1`} download>
              {r.files.length > 1 ? t('„{name}“ herunterladen', { name: file.name }) : t('Herunterladen')}
            </a>
            <ShareFile url={`${fileUrl}?download=1`} name={file.name} />
          </div>
        </>
      )}
      <Comments r={r} reload={res.reload} />
    </Sheet>
  );
}

function Comments({ r, reload }: { r: RequestDetail; reload(): Promise<void> }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.post(`/api/app/anfrage/${r.id}/comments`, { body });
      setText('');
      await reload();
      toast(t('Kommentar an Ihre Kanzlei gesendet'));
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rthread">
      <div className="rt-h">{t('Kommentare')}</div>
      {r.comments.map((c) => (
        <div key={c.id} className={`rc${c.mine ? ' rc-me' : ''}`}>
          <b>{c.mine ? t('Sie') : tx(c.authorName)}</b> <span>{fmtDateTime(c.createdAt)}</span>
          <div>{c.body}</div>
        </div>
      ))}
      <form className="rc-form" onSubmit={submit}>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={1000}
          placeholder={t('Kommentar an Ihre Kanzlei')}
          aria-label={t('Kommentar zu {name}', { name: tx(r.label) })}
        />
        <button type="submit" className="btn2" disabled={busy}>
          {t('Senden')}
        </button>
      </form>
    </div>
  );
}
